import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Send, Loader2, Bot, User, FileText, Paperclip, FileSpreadsheet, List, Languages, BarChart3, Heart, CheckSquare, Hash, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { CostConfirmDialog } from "@/components/CostConfirmDialog";
import { estimateChatQueryCost } from "@/utils/costEstimation";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

interface Message {
  role: "user" | "assistant";
  content: string;
  isPdfAnalysis?: boolean;
  fileName?: string;
  pdfUrl?: string;
}

interface AiChatPanelProps {
  chatContext: string;
  language: string;
  onClose?: () => void;
  onCostIncurred?: (cost: number) => void;
}

const AiChatPanel = ({ chatContext, language, onCostIncurred }: AiChatPanelProps) => {
  const { t } = useLanguage();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [pendingMessage, setPendingMessage] = useState("");
  const [currentBudget, setCurrentBudget] = useState<number | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAnalyzingPdf, setIsAnalyzingPdf] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .single();

      setIsAdmin(!!data);
    };

    checkAdminStatus();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendClick = async () => {
    if (!input.trim() || isLoading) return;

    // Fetch current budget
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("budget")
          .eq("id", user.id)
          .single();
        
        if (data) {
          setCurrentBudget(parseFloat(String(data.budget || 0)));
        }
      }
    } catch (error) {
      console.error("Error fetching budget:", error);
    }

    // Calculate cost and show confirmation
    const cost = estimateChatQueryCost(messages.length, isAdmin);
    setEstimatedCost(cost);
    setPendingMessage(input);
    setShowCostDialog(true);
  };

  const sendMessage = async () => {
    // Check and deduct budget
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("budget")
          .eq("id", user.id)
          .single();

        if (profile) {
          const currentBudget = parseFloat(String(profile.budget || 0));
          if (currentBudget < estimatedCost) {
            toast.error(t("toast.insufficientBudget"));
            setShowCostDialog(false);
            return;
          }

          // Deduct from budget
          const newBudget = currentBudget - estimatedCost;
          await supabase
            .from("profiles")
            .update({ budget: newBudget })
            .eq("id", user.id);
        }
      }
    } catch (error) {
      console.error("Error handling budget:", error);
      toast.error(t("toast.budgetProcessFailed"));
      setShowCostDialog(false);
      return;
    }

    const userMessage: Message = { role: "user", content: pendingMessage };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setPendingMessage("");
    setShowCostDialog(false);
    setIsLoading(true);

    try {
      // Get user session for authenticated request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("User not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-query`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [...messages, userMessage],
            chatContext,
            language,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      const data = await response.json();
      const assistantMessage: Message = {
        role: "assistant",
        content: data.response,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Track cost
      if (onCostIncurred) {
        onCostIncurred(estimatedCost);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage: Message = {
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error(t("aiChat.onlyPdfAllowed"));
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error(t("aiChat.fileTooLarge"));
      return;
    }

    setSelectedFile(file);
    setIsAnalyzingPdf(true);

    try {
      // Upload PDF to Supabase storage
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("toast.mustBeLoggedIn"));
        return;
      }

      const fileName = `pdf-analysis/${user.id}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("chat-exports")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("chat-exports")
        .getPublicUrl(fileName);

      const pdfUrl = urlData.publicUrl;

      // Add user message with PDF URL
      const userMessage: Message = {
        role: "user",
        content: `📄 ${file.name}`,
        isPdfAnalysis: true,
        fileName: file.name,
        pdfUrl,
      };
      setMessages((prev) => [...prev, userMessage]);

      // Load PDF.js library
      const pdfjsLib = await import("pdfjs-dist");
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

      // Read PDF file
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let pdfText = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: any) => item.str).join(" ");
        pdfText += pageText + "\n";
      }

      console.log("Extracted PDF text length:", pdfText.length);

      // Call analyze-pdf edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-pdf`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            pdfText,
            language,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to analyze PDF");
      }

      const data = await response.json();
      
      const assistantMessage: Message = {
        role: "assistant",
        content: data.analysis,
      };
      setMessages((prev) => [...prev, assistantMessage]);

    } catch (error) {
      console.error("Error analyzing PDF:", error);
      toast.error(t("aiChat.pdfAnalysisFailed"));
      const errorMessage: Message = {
        role: "assistant",
        content: t("aiChat.pdfAnalysisError"),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsAnalyzingPdf(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <>
      <Card className={cn(
        "shadow-lg transition-all duration-300 bg-background",
        isExpanded ? "w-96 h-[500px]" : "w-16 h-16"
      )}>
        {!isExpanded ? (
          <Button
            onClick={() => setIsExpanded(true)}
            className="w-full h-full rounded-lg"
            size="icon"
          >
            <Bot className="w-6 h-6" />
          </Button>
        ) : (
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-sm">{t("aiChat.assistant") || "AI Assistant"}</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="h-6 w-6 p-0"
              >
                ×
              </Button>
            </div>

            {/* Quick Actions */}
            {messages.length === 0 && (
              <div className="p-3 border-b">
                <p className="text-xs text-muted-foreground mb-2">פעולות מהירות:</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("סכם את השיחה הזו בצורה תמציתית")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    סיכום
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("צור טבלה של המידע החשוב בשיחה")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <FileSpreadsheet className="w-3 h-3 mr-1" />
                    טבלה
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("חלץ את נקודות המפתח העיקריות מהשיחה")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <List className="w-3 h-3 mr-1" />
                    נקודות מפתח
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("נתח את הסטטיסטיקות של השיחה")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <BarChart3 className="w-3 h-3 mr-1" />
                    סטטיסטיקות
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("תרגם את השיחה לאנגלית")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <Languages className="w-3 h-3 mr-1" />
                    תרגום
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("נתח את הסנטימנט והטון הרגשי של השיחה - חיובי, שלילי וניטרלי לכל משתתף")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <Heart className="w-3 h-3 mr-1" />
                    סנטימנט
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("חלץ את כל המשימות, ההבטחות ופריטי המעקב שהוזכרו בשיחה")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <CheckSquare className="w-3 h-3 mr-1" />
                    משימות
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("זהה ורשום את הנושאים והנושאי המשנה העיקריים שנדונו בשיחה")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <Hash className="w-3 h-3 mr-1" />
                    נושאים
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput("הצג תובנות על המשתתפים: מי מדבר הכי הרבה, דפוסי פעילות, וסגנון תקשורת של כל אחד")}
                    disabled={isLoading || isAnalyzingPdf}
                    className="h-7 text-xs"
                  >
                    <Users className="w-3 h-3 mr-1" />
                    משתתפים
                  </Button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center text-muted-foreground text-xs py-8">
                  <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{t("aiChat.askAbout") || "Ask questions about this conversation"}</p>
                </div>
              )}
              
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex gap-2 text-xs",
                    message.role === "user" ? "justify-end" : "justify-start"
                  )}
                >
                  {message.role === "assistant" && (
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-3 h-3 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      "rounded-lg px-3 py-2 max-w-[80%]",
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    )}
                  >
                    {message.isPdfAnalysis && message.pdfUrl && (
                      <div className="flex items-center gap-2 mb-2 text-xs">
                        <FileText className="w-3 h-3" />
                        <a 
                          href={message.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:no-underline"
                        >
                          {message.fileName}
                        </a>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap break-words">{message.content}</p>
                  </div>
                  {message.role === "user" && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <User className="w-3 h-3 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && (
                <div className="flex gap-2 justify-start">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="w-3 h-3 text-primary" />
                  </div>
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3 border-t">
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || isAnalyzingPdf}
                  className="h-8 w-8 p-0"
                >
                  <Paperclip className="w-3 h-3" />
                </Button>
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && handleSendClick()}
                  placeholder={t("aiChat.askAbout") || "Ask questions..."}
                  disabled={isLoading || isAnalyzingPdf}
                  className="text-xs h-8"
                />
                <Button
                  onClick={handleSendClick}
                  disabled={!input.trim() || isLoading || isAnalyzingPdf}
                  size="sm"
                  className="h-8 w-8 p-0"
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
              {selectedFile && (
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <FileText className="w-3 h-3" />
                  <span className="truncate flex-1">{selectedFile.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                    className="h-4 w-4 p-0"
                  >
                    ×
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {showCostDialog && (
        <CostConfirmDialog
          open={showCostDialog}
          onOpenChange={setShowCostDialog}
          onConfirm={sendMessage}
          cost={estimatedCost}
          title="Confirm AI Query"
          description="This action will use AI credits from your budget."
          currentBudget={currentBudget}
        />
      )}
    </>
  );
};

export default AiChatPanel;